import {
    useCallback,
    useMemo,
    useRef,
    type FC,
    useId
} from "react";
import type {
    View
} from "react-native";
import type IMenubarMenuProps from "./type";
import {
    usePointerType
} from "../../../../helpers/pointer";
import {
    MenubarMenuContext,
    useMenubarContext
} from "../../context";
import type {
    DropdownMenuContextType
} from "../../../dropdownMenu/type";
import type {
    MenubarMenuContextType
} from "../../type";
import {
    DropdownMenuContext
} from "../../../dropdownMenu/context";
import {
    getMenubarElements
} from "../../util";
import MenubarTrigger from "../trigger";

const MenubarMenu: FC<IMenubarMenuProps> = ({
    children,
    menuKey
}) => {
    const context = useMenubarContext({
        part: "Menu"
    });

    const pointerType = usePointerType();

    const triggerId = useId();

    const contentId = useId();

    const triggerRef = useRef<View | null>(null);

    const anchorRef = useRef<View | null>(null);

    const pointerAnchorRef = useRef<View | null>(null);

    const handleOpenChangeProp = context?.handleOpenChange;

    const handleCloseProp = context?.handleClose;

    const handleTabKeyProp = context?.handleTabKey;

    const isOpen = context?.openKey === menuKey;

    const title = getMenubarElements({
        type: MenubarTrigger,
        children
    })[0]?.props.title;

    const handleOpenChange = useCallback((nextIsOpen: boolean, nextOpenMode?: DropdownMenuContextType["openMode"]) => {
        if(nextIsOpen) {
            handleOpenChangeProp?.(menuKey, nextOpenMode);

            return;
        }

        handleCloseProp?.(menuKey);
    }, [
        handleOpenChangeProp,
        handleCloseProp,
        menuKey
    ]);

    const handleClose = useCallback(() => {
        handleCloseProp?.(menuKey);
    }, [
        handleCloseProp,
        menuKey
    ]);

    const handleTabKey = useCallback(() => {
        handleTabKeyProp?.(menuKey);
    }, [
        handleTabKeyProp,
        menuKey
    ]);

    const handleNoop = useCallback(() => {}, []);

    const dropdownValue = useMemo<DropdownMenuContextType>(() => ({
        openMode: context?.openMode ?? "pointer",
        customLocalize: context?.customLocalize,
        handleTriggerWidthChange: handleNoop,
        customTheme: context?.customTheme,
        isSheet: pointerType === "coarse",
        handlePointerChange: handleNoop,
        isDisabled: false,
        handleOpenChange,
        kind: "dropdown",
        pointerAnchorRef,
        triggerWidth: 0,
        align: "start",
        side: "bottom",
        pointer: null,
        handleTabKey,
        handleClose,
        anchorRef,
        contentId,
        triggerId,
        isOpen,
        title
    }), [
        context?.customLocalize,
        context?.customTheme,
        handleOpenChange,
        context?.openMode,
        handleTabKey,
        pointerType,
        handleClose,
        handleNoop,
        contentId,
        triggerId,
        isOpen,
        title
    ]);

    const menuValue = useMemo<MenubarMenuContextType>(() => ({
        triggerRef,
        anchorRef,
        contentId,
        triggerId,
        menuKey,
        isOpen
    }), [
        contentId,
        triggerId,
        menuKey,
        isOpen
    ]);

    if(!context) {
        return null;
    }

    return <MenubarMenuContext.Provider
        value={menuValue}
    >
        <DropdownMenuContext.Provider
            value={dropdownValue}
        >
            {children}
        </DropdownMenuContext.Provider>
    </MenubarMenuContext.Provider>;
};
export default MenubarMenu;
